import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIdCard } from '@fortawesome/free-solid-svg-icons';
import {
  PENDING_GENERAL_DL_VERIFICATIONS,
  REVIEW_GENERAL_DL_VERIFICATION,
} from '../../services/adminGeneralDLService/queries';
import { URLS } from '../../../constants/urls';
import { Modal } from '../../components/modal';
import { Button } from '../../components/button';

import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardRow,
  EmptyState,
  LoadingBlock,
  PageContainer,
  PageHeader,
} from '../../components/ui';

type PendingGeneralDL = {
  id: string;
  userId: string;
  frontDriverLicenseUrl: string;
  backDriverLicenseUrl: string;
  /** Spec 026: signed links from the API. */
  frontImageUrl?: string | null;
  backImageUrl?: string | null;
  expirationDate: string;
  birthDate?: string | null;
  legalFirstName?: string | null;
  legalMiddleName?: string | null;
  legalLastName?: string | null;
  isValid: boolean;
  isVerifiedByAdmin: boolean;
  submittedAt?: string | null;
  canBeAdditionalDriver: boolean;
  licenceNumberLast4?: string | null;
  licenceNumber?: string | null;
  issueDate?: string | null;
  issuingRegion?: string | null;
  submittedBrandSite?: string | null;
  submittedVia?: string | null;
  recheckRequestedAt?: string | null;
  recheckNote?: string | null;
  user?: {
    id: string;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
  } | null;
};

const ModalLead = styled.p`
  ${tw`text-sm mb-4`}
  color: var(--color-text-secondary);
`;

const UserInfo = styled.div`
  ${tw`flex-1 min-w-0`}
`;

const UserName = styled.h3`
  ${tw`text-sm font-semibold`}
  color: var(--color-text-primary);
`;

const UserEmail = styled.p`
  ${tw`text-sm mt-0.5 break-all`}
  color: var(--color-text-secondary);
`;

const UserMeta = styled.p`
  ${tw`text-xs mt-1`}
  color: var(--color-text-tertiary);
`;

const PhotoGrid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 gap-4 mb-4`}
`;

const PhotoCard = styled.div`
  ${tw`flex flex-col`}
`;

const PhotoLabel = styled.label`
  ${tw`text-sm font-medium mb-2`}
  color: var(--color-text-primary);
`;

const HostPhotoPreview = styled.img`
  ${tw`w-full h-48 object-contain rounded-lg cursor-pointer`}
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
`;

const VerificationChecklist = styled.ul`
  ${tw`list-disc list-inside text-sm mb-4 space-y-1 p-4 rounded-lg`}
  color: var(--color-text-secondary);
  background-color: var(--color-bg-secondary);
`;

const FormRow = styled.div`
  ${tw`flex flex-col gap-1.5 mb-4`}
`;

const FormLabel = styled.label`
  ${tw`text-sm font-medium`}
  color: var(--color-text-primary);
`;

const FormInput = styled.input`
  ${tw`w-full h-10 px-3`}
`;

const ToggleRow = styled.label`
  ${tw`flex items-start gap-3 text-sm cursor-pointer p-3 rounded-lg`}
  color: var(--color-text-secondary);
  background-color: var(--color-bg-secondary);
`;

const ButtonGroup = styled.div`
  ${tw`flex flex-wrap gap-3 mt-5`}
`;

const RejectTextArea = styled.textarea`
  ${tw`w-full p-3 resize-none`}
  min-height: 100px;
`;

const FullImageOverlay = styled.div`
  ${tw`fixed inset-0 z-[100000] bg-black bg-opacity-80 flex items-center justify-center p-4`}
`;

const FullImage = styled.img`
  ${tw`max-w-full max-h-full object-contain min-w-0 box-border`}
`;

function toDLImageUrl(keyOrUrl: string): string {
  if (keyOrUrl.startsWith('http://') || keyOrUrl.startsWith('https://')) {
    return keyOrUrl;
  }
  return `${URLS.S3_BUCKET.DL_VERIFICATION}/${keyOrUrl}`;
}

function toDateInputValue(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().slice(0, 10);
}

function getUserDisplayName(record: PendingGeneralDL): string {
  const user = record.user;
  if (!user) {
    return record.userId;
  }
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return fullName || user.displayName || user.email;
}

export function AdminVerifyGeneralDriversLicensePage() {
  const { data, loading, error } = useQuery(PENDING_GENERAL_DL_VERIFICATIONS);
  const [reviewGeneralDL, { loading: reviewing }] = useMutation(REVIEW_GENERAL_DL_VERIFICATION, {
    refetchQueries: [{ query: PENDING_GENERAL_DL_VERIFICATIONS }],
  });

  const [selected, setSelected] = useState<PendingGeneralDL | null>(null);
  const [expirationDate, setExpirationDate] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [legalFirstName, setLegalFirstName] = useState('');
  const [legalMiddleName, setLegalMiddleName] = useState('');
  const [legalLastName, setLegalLastName] = useState('');
  const [isValid, setIsValid] = useState(true);
  // Spec 026: copied from the photo by the reviewing admin.
  const [licenceNumber, setLicenceNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [issuingRegion, setIssuingRegion] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [fullImageUrl, setFullImageUrl] = useState<string | null>(null);

  const submissions: PendingGeneralDL[] = useMemo(
    () => data?.pendingGeneralDLVerifications ?? [],
    [data],
  );

  const openReviewModal = (record: PendingGeneralDL) => {
    setSelected(record);
    setExpirationDate(toDateInputValue(record.expirationDate));
    setBirthDate(record.birthDate ? toDateInputValue(record.birthDate) : '');
    setLegalFirstName(record.legalFirstName?.trim() ?? '');
    setLegalMiddleName(record.legalMiddleName?.trim() ?? '');
    setLegalLastName(record.legalLastName?.trim() ?? '');
    setIsValid(true);
    setLicenceNumber(record.licenceNumber ?? '');
    setIssueDate(record.issueDate ? toDateInputValue(record.issueDate) : '');
    setIssuingRegion(record.issuingRegion ?? '');
    setShowRejectForm(false);
    setRejectionReason('');
  };

  /** Spec 026: the licence number, issue date and place of issue, when entered. */
  const licenceFields = () => ({
    licenceNumber: licenceNumber.trim() || undefined,
    issueDate: issueDate ? new Date(issueDate).toISOString() : undefined,
    issuingRegion: issuingRegion.trim() || undefined,
  });

  const closeReviewModal = () => {
    setSelected(null);
    setShowRejectForm(false);
    setRejectionReason('');
  };

  const handleApprove = async () => {
    if (!selected) {
      return;
    }
    if (!expirationDate) {
      toast.error('Expiration date is required');
      return;
    }
    if (!birthDate) {
      toast.error('Birth date is required — confirm DOB matches the license');
      return;
    }
    const lf = legalFirstName.trim().slice(0, 120);
    const lm = legalMiddleName.trim().slice(0, 120);
    const ll = legalLastName.trim().slice(0, 120);
    if (!lf) {
      toast.error('Legal first name is required');
      return;
    }
    if (!ll) {
      toast.error('Legal last name is required');
      return;
    }

    try {
      await reviewGeneralDL({
        variables: {
          input: {
            userId: selected.userId,
            approved: true,
            expirationDate: new Date(expirationDate).toISOString(),
            birthDate: new Date(birthDate).toISOString(),
            legalFirstName: lf,
            legalMiddleName: lm || undefined,
            legalLastName: ll,
            ...licenceFields(),
            isValid,
            rejectionReason: isValid
              ? undefined
              : rejectionReason.trim() ||
                'License verified but marked invalid for additional driver use.',
          },
        },
      });
      toast.success(
        isValid
          ? 'Driver license approved.'
          : 'Review saved — license marked invalid for additional driver use.',
      );
      closeReviewModal();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to approve driver license');
    }
  };

  const handleReject = async () => {
    if (!selected) {
      return;
    }
    if (!rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      await reviewGeneralDL({
        variables: {
          input: {
            userId: selected.userId,
            approved: false,
            rejectionReason: rejectionReason.trim(),
            expirationDate: expirationDate ? new Date(expirationDate).toISOString() : undefined,
            birthDate: birthDate ? new Date(birthDate).toISOString() : undefined,
            legalFirstName: legalFirstName.trim().slice(0, 120) || undefined,
            legalMiddleName: legalMiddleName.trim().slice(0, 120) || undefined,
            legalLastName: legalLastName.trim().slice(0, 120) || undefined,
            ...licenceFields(),
          },
        },
      });
      toast.success('Driver license rejected.');
      closeReviewModal();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to reject driver license');
    }
  };

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Admin"
        title="Driver license review"
        description="Account-level driver licenses submitted on this site, shared by every site a guest uses. A license that ends before a trip does cannot book it. This is separate from the check at pickup."
      />

      <Card>
        <CardHeader
          title="Pending submissions"
          description={loading ? undefined : `${submissions.length} waiting for review.`}
        />
        {loading && <LoadingBlock label="Loading submissions" />}

        {error && (
          <CardBody>
            <Alert tone="error" title="Error loading submissions">
              {error.message}
            </Alert>
          </CardBody>
        )}

        {!loading && !error && submissions.length === 0 && (
          <EmptyState
            icon={faIdCard}
            title="No pending verifications"
            description="New driver license submissions will appear here."
          />
        )}

        {!loading &&
          submissions.map(record => (
            <CardRow key={record.id} $interactive onClick={() => openReviewModal(record)}>
              <UserInfo>
                <UserName>{getUserDisplayName(record)}</UserName>
                <UserEmail>{record.user?.email}</UserEmail>
                <UserMeta>
                  Submitted:{' '}
                  {record.submittedAt ? new Date(record.submittedAt).toLocaleString() : 'Unknown'} ·
                  Expires: {new Date(record.expirationDate).toLocaleDateString()}
                  {record.birthDate
                    ? ` · DOB: ${new Date(record.birthDate).toLocaleDateString()}`
                    : ''}
                  {[record.legalFirstName, record.legalMiddleName, record.legalLastName].some(
                    Boolean,
                  )
                    ? ` · Legal (DL): ${[record.legalFirstName, record.legalMiddleName, record.legalLastName].filter(Boolean).join(' ')}`
                    : ''}
                  {record.submittedBrandSite
                    ? ` · Submitted on ${record.submittedBrandSite}${record.submittedVia ? ` (${record.submittedVia.replace('_', ' ')})` : ''}`
                    : ''}
                </UserMeta>
                {record.recheckRequestedAt && (
                  <UserMeta>Recheck requested: {record.recheckNote}</UserMeta>
                )}
              </UserInfo>
              <Badge tone="warning" dot>
                {record.recheckRequestedAt ? 'Recheck requested' : 'Pending review'}
              </Badge>
            </CardRow>
          ))}
      </Card>

      <Modal
        isOpen={!!selected}
        onClose={closeReviewModal}
        title="Review driver license"
        modalWidth="min(900px, calc(100vw - 40px))"
      >
        {selected && (
          <div>
            <ModalLead>
              {getUserDisplayName(selected)} · {selected.user?.email}
            </ModalLead>

            <PhotoGrid>
              <PhotoCard>
                <PhotoLabel>Front of driver&apos;s license</PhotoLabel>
                <HostPhotoPreview
                  src={selected.frontImageUrl || toDLImageUrl(selected.frontDriverLicenseUrl)}
                  alt="Front DL"
                  onClick={() =>
                    setFullImageUrl(
                      selected.frontImageUrl || toDLImageUrl(selected.frontDriverLicenseUrl),
                    )
                  }
                />
              </PhotoCard>
              <PhotoCard>
                <PhotoLabel>Back of driver&apos;s license</PhotoLabel>
                <HostPhotoPreview
                  src={selected.backImageUrl || toDLImageUrl(selected.backDriverLicenseUrl)}
                  alt="Back DL"
                  onClick={() =>
                    setFullImageUrl(
                      selected.backImageUrl || toDLImageUrl(selected.backDriverLicenseUrl),
                    )
                  }
                />
              </PhotoCard>
            </PhotoGrid>

            <VerificationChecklist>
              <li>Verify name matches the user&apos;s account</li>
              <li>Verify date of birth is visible and valid</li>
              <li>Verify license is not expired (any country accepted; passports not supported)</li>
              <li>Document is a driver&apos;s license, not a passport</li>
              <li>Copy the license number, issue date and place of issue from the photo</li>
            </VerificationChecklist>

            <FormRow>
              <FormLabel htmlFor="admin-dl-legal-first">Legal first name</FormLabel>
              <FormInput
                id="admin-dl-legal-first"
                type="text"
                maxLength={120}
                value={legalFirstName}
                onChange={event => setLegalFirstName(event.target.value)}
                autoCapitalize="words"
              />
            </FormRow>

            <FormRow>
              <FormLabel htmlFor="admin-dl-legal-middle">Legal middle name (optional)</FormLabel>
              <FormInput
                id="admin-dl-legal-middle"
                type="text"
                maxLength={120}
                value={legalMiddleName}
                onChange={event => setLegalMiddleName(event.target.value)}
                autoCapitalize="words"
              />
            </FormRow>

            <FormRow>
              <FormLabel htmlFor="admin-dl-legal-last">Legal last name</FormLabel>
              <FormInput
                id="admin-dl-legal-last"
                type="text"
                maxLength={120}
                value={legalLastName}
                onChange={event => setLegalLastName(event.target.value)}
                autoCapitalize="words"
              />
            </FormRow>

            <FormRow>
              <FormLabel htmlFor="admin-dl-expiration">Expiration date</FormLabel>
              <FormInput
                id="admin-dl-expiration"
                type="date"
                value={expirationDate}
                onChange={event => setExpirationDate(event.target.value)}
              />
            </FormRow>

            <FormRow>
              <FormLabel htmlFor="admin-dl-number">
                License number
                {selected.licenceNumberLast4 && !licenceNumber
                  ? ` (on file, ends in ${selected.licenceNumberLast4})`
                  : ''}
              </FormLabel>
              <FormInput
                id="admin-dl-number"
                type="text"
                maxLength={40}
                autoComplete="off"
                value={licenceNumber}
                onChange={event => setLicenceNumber(event.target.value)}
              />
            </FormRow>

            <FormRow>
              <FormLabel htmlFor="admin-dl-issue">Issue date</FormLabel>
              <FormInput
                id="admin-dl-issue"
                type="date"
                value={issueDate}
                onChange={event => setIssueDate(event.target.value)}
              />
            </FormRow>

            <FormRow>
              <FormLabel htmlFor="admin-dl-region">Place of issue (state or country)</FormLabel>
              <FormInput
                id="admin-dl-region"
                type="text"
                maxLength={80}
                value={issuingRegion}
                onChange={event => setIssuingRegion(event.target.value)}
              />
            </FormRow>

            <FormRow>
              <FormLabel htmlFor="admin-dl-birth">Birth date (DOB)</FormLabel>
              <FormInput
                id="admin-dl-birth"
                type="date"
                value={birthDate}
                onChange={event => setBirthDate(event.target.value)}
              />
            </FormRow>

            <ToggleRow>
              <input
                type="checkbox"
                checked={isValid}
                onChange={event => setIsValid(event.target.checked)}
              />
              <span>
                Valid for additional driver use (<code>isValid</code>). Uncheck if verified but not
                eligible to drive on bookings.
              </span>
            </ToggleRow>

            {showRejectForm && (
              <FormRow>
                <FormLabel htmlFor="admin-dl-reject-reason">Rejection reason</FormLabel>
                <RejectTextArea
                  id="admin-dl-reject-reason"
                  value={rejectionReason}
                  onChange={event => setRejectionReason(event.target.value)}
                  placeholder="Required when rejecting"
                />
              </FormRow>
            )}

            <ButtonGroup>
              <Button
                text={reviewing ? 'Saving...' : 'Approve'}
                onClick={handleApprove}
                buttonWidth="140px"
                theme="filled"
                isLoading={reviewing}
                isDisabled={reviewing}
              />
              {!showRejectForm ? (
                <Button
                  text="Reject"
                  onClick={() => setShowRejectForm(true)}
                  buttonWidth="140px"
                  theme="outlined"
                  isDisabled={reviewing}
                />
              ) : (
                <Button
                  text={reviewing ? 'Rejecting...' : 'Confirm reject'}
                  onClick={handleReject}
                  buttonWidth="160px"
                  theme="outlined"
                  isLoading={reviewing}
                  isDisabled={reviewing}
                />
              )}
            </ButtonGroup>
          </div>
        )}
      </Modal>

      {fullImageUrl && (
        <FullImageOverlay onClick={() => setFullImageUrl(null)} role="presentation">
          <FullImage src={fullImageUrl} alt="Driver license full view" />
        </FullImageOverlay>
      )}
    </PageContainer>
  );
}

export default AdminVerifyGeneralDriversLicensePage;
